import {
  Button,
  Checkbox,
  ChipToggle,
  ChoiceRow,
  Field,
  FieldGrid,
  Input,
  Radio,
  RadioGroup,
  ReorderControls,
  Select,
  Textarea,
} from '@westhetics/ui'
import { useMemo, useState } from 'react'
import {
  CLINIC_QUESTION_TYPES,
  QUESTION_TYPES,
  type FlagRuleWhen,
  type FollowUpWhen,
  type QuestionType,
} from './api'
import {
  deriveKey,
  deriveOptionValue,
  emptyFlagRule,
  emptyFollowUp,
  emptyOption,
  flagRuleWhens,
  followUpWhens,
  isChoiceType,
  moveItem,
  validateQuestion,
  type FieldErrorMap,
  type OptionDraft,
  type QuestionDraft,
} from './questionnaire-form'
import { TYPE_LABELS, answerChoices } from './questionnaire-labels'

/**
 * Edição de uma pergunta do questionário (docs/areas/anamnese.md, "Telas · Questionário"). O mesmo
 * formulário serve à gestora (`mode: 'clinic'`: quatro tipos, sem condição, sem chave à vista,
 * P-ANA-64) e ao root (`mode: 'default'`: os sete tipos, chave, condição, sensível e revisão). Erro
 * nasce ao sair do campo, com as mensagens da API; depois de uma tentativa de salvar, todos aparecem.
 */

export type QuestionFormMode = 'clinic' | 'default'

const OPTION_LIMIT = 12

export function QuestionForm({
  question,
  onChange,
  mode,
  previous,
  takenKeys,
  serverErrors = {},
  showAllErrors = false,
  onClose,
  onRemove,
}: {
  question: QuestionDraft
  onChange: (next: QuestionDraft) => void
  mode: QuestionFormMode
  /** As perguntas anteriores do mesmo bloco (a condição só aponta para elas). */
  previous: readonly QuestionDraft[]
  /** As chaves das outras perguntas, para a chave desta não repetir. */
  takenKeys: ReadonlySet<string>
  /** O que a API devolveu no 422 para esta pergunta, já sem o prefixo do caminho. */
  serverErrors?: FieldErrorMap
  /** Depois de tentar salvar: mostra todo erro, não só o do campo que a pessoa deixou. */
  showAllErrors?: boolean
  onClose: () => void
  onRemove: () => void
}) {
  const isClinic = mode === 'clinic'
  const [touched, setTouched] = useState<Set<string>>(() => new Set())
  const errors = useMemo(
    () => validateQuestion(question, { isClinic, previous, takenKeys }),
    [question, isClinic, previous, takenKeys],
  )

  /** O erro que a tela mostra: o próprio, se o campo já foi deixado; senão, o da API. */
  const errorAt = (path: string) =>
    (showAllErrors || touched.has(path) ? errors[path] : undefined) ?? serverErrors[path]
  const touch = (path: string) =>
    setTouched((current) => (current.has(path) ? current : new Set(current).add(path)))

  const update = (patch: Partial<QuestionDraft>) => onChange({ ...question, ...patch })
  const choice = isChoiceType(question.type)
  const types = isClinic ? CLINIC_QUESTION_TYPES : QUESTION_TYPES

  function changeType(type: QuestionType) {
    const next: QuestionDraft = { ...question, type }
    if (isChoiceType(type) && next.options.length < 2) {
      next.options = [...next.options, emptyOption(), emptyOption()].slice(0, 2)
    }
    if (!isChoiceType(type)) next.options = []
    if (type !== 'multiple_choice') {
      next.options = next.options.map((option) => ({ ...option, isExclusive: false }))
    }
    // O que o tipo novo não aceita sai, em vez de virar erro: a pessoa redecide depois.
    if (type === 'text') next.followUp = null
    if (next.followUp) {
      const allowed = followUpWhens(type)
      if (!allowed.includes(next.followUp.when)) next.followUp.when = allowed[0]!
    }
    if (next.flagRule) {
      const allowed = flagRuleWhens(type)
      next.flagRule = allowed.length === 0 ? null : { ...next.flagRule, when: allowed[0]! }
    }
    if (type !== 'yes_no') next.allowsUnsure = false
    onChange(next)
  }

  function updateOption(index: number, patch: Partial<OptionDraft>) {
    update({
      options: question.options.map((option, i) =>
        i === index ? { ...option, ...patch } : option,
      ),
    })
  }

  function blurOptionLabel(index: number) {
    touch(`options.${index}.label`)
    const option = question.options[index]
    if (!option || option.value.trim() || !option.label.trim()) return
    const taken = new Set(
      question.options.filter((_, i) => i !== index).map((item) => item.value.trim()),
    )
    updateOption(index, { value: deriveOptionValue(option.label, taken) })
  }

  function blurLabel() {
    touch('label')
    if (!question.label.trim()) return
    // A chave nasce do texto na primeira vez e fica (P-ANA-64). Na clínica, se a API recusou a chave
    // (colide com uma do padrão), o texto reescrito gera outra.
    if (!question.key.trim() || (isClinic && serverErrors.key)) {
      update({ key: deriveKey(question.label, takenKeys) })
    }
  }

  function toggleFollowUp(on: boolean) {
    if (!on) {
      update({
        followUp: null,
        options: question.options.map((option) => ({ ...option, opensFollowUp: false })),
      })
      return
    }
    update({ followUp: emptyFollowUp(followUpWhens(question.type)[0]!) })
  }

  function toggleFlagRule(on: boolean) {
    if (!on) {
      update({
        flagRule: null,
        options: question.options.map((option) => ({
          ...option,
          raisesFlag: false,
          attentionLabel: '',
        })),
      })
      return
    }
    update({ flagRule: emptyFlagRule(flagRuleWhens(question.type)[0]!) })
  }

  // Só pergunta anterior do mesmo bloco, que apareça sempre e tenha respostas fixas (RN-ANA-11).
  const conditionSources = previous.filter(
    (item) => !item.condition && item.key.trim() && answerChoices(item).length > 0,
  )
  const conditionSource = question.condition
    ? conditionSources.find((item) => item.key === question.condition?.questionKey)
    : undefined

  const fieldId = (path: string) => `question-${question.uid}-${path}`

  return (
    <div
      role="group"
      aria-label={question.label.trim() ? `Editar "${question.label.trim()}"` : 'Nova pergunta'}
      className="border-hairline bg-pampas flex flex-col gap-4 border px-4 py-4"
    >
      {serverErrors[''] ? (
        <p role="alert" className="font-body text-eyebrow text-plum leading-[1.5] font-light">
          {serverErrors['']}
        </p>
      ) : null}

      <Field
        label="Pergunta"
        id={fieldId('label')}
        meta={`${question.label.trim().length}/200`}
        // Na clínica a chave não aparece: o que a API disser dela vale para o texto da pergunta.
        error={errorAt('label') ?? (isClinic ? serverErrors.key : undefined)}
        hint="Como a paciente lê, em uma frase. Sem HTML."
      >
        <Input
          value={question.label}
          maxLength={200}
          onChange={(event) => update({ label: event.target.value })}
          onBlur={blurLabel}
        />
      </Field>

      <FieldGrid>
        <Field label="Tipo" id={fieldId('type')} error={errorAt('type')}>
          <Select
            value={question.type}
            onChange={(event) => changeType(event.target.value as QuestionType)}
          >
            {types.map((type) => (
              <option key={type} value={type}>
                {TYPE_LABELS[type]}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Rótulo curto"
          id={fieldId('shortLabel')}
          meta={`${question.shortLabel.trim().length}/40`}
          error={errorAt('shortLabel')}
          hint="Como aparece na leitura da clínica e no documento, em caixa alta."
        >
          <Input
            value={question.shortLabel}
            maxLength={40}
            onChange={(event) => update({ shortLabel: event.target.value.toUpperCase() })}
            onBlur={() => touch('shortLabel')}
          />
        </Field>
        {isClinic ? null : (
          <Field
            label="Chave"
            id={fieldId('key')}
            error={errorAt('key')}
            hint="Identidade estável entre versões, como continuousMedication. Chave nova é pergunta nova."
          >
            <Input
              value={question.key}
              maxLength={41}
              autoComplete="off"
              onChange={(event) => update({ key: event.target.value })}
              onBlur={() => touch('key')}
            />
          </Field>
        )}
      </FieldGrid>

      <Field
        label="Contexto"
        optional
        id={fieldId('context')}
        meta={`${question.context.trim().length}/300`}
        error={errorAt('context')}
        hint="A frase sob a pergunta que explica por que ela existe, como 'Anticoagulantes, isotretinoína e anticoncepcional contam.'"
      >
        <Textarea
          rows={2}
          value={question.context}
          maxLength={300}
          onChange={(event) => update({ context: event.target.value })}
          onBlur={() => touch('context')}
        />
      </Field>

      <div className="border-hairline bg-surface border">
        <ChoiceRow
          bare
          control={
            <Checkbox
              checked={question.isRequired}
              onCheckedChange={(checked) => update({ isRequired: checked === true })}
            />
          }
          description="Só a pergunta opcional recebe a marca '· opcional' na tela."
        >
          Obrigatória
        </ChoiceRow>
        <ChoiceRow
          bare
          control={
            <Checkbox
              checked={question.isSkippable}
              onCheckedChange={(checked) => update({ isSkippable: checked === true })}
            />
          }
          description="Mostra 'Prefere não responder? Pular esta pergunta'. A leitura diz 'Preferiu não responder'."
        >
          Pode pular
        </ChoiceRow>
        {question.type === 'yes_no' ? (
          <ChoiceRow
            bare
            control={
              <Checkbox
                checked={question.allowsUnsure}
                onCheckedChange={(checked) => update({ allowsUnsure: checked === true })}
              />
            }
            description="Terceira opção, depois de Sim e Não."
          >
            Inclui "Não sei dizer"
          </ChoiceRow>
        ) : null}
        {isClinic ? null : (
          <ChoiceRow
            bare
            control={
              <Checkbox
                checked={question.isSensitive}
                onCheckedChange={(checked) => update({ isSensitive: checked === true })}
              />
            }
            description="Ganha a frase de explicação na tela, mesmo fora do bloco sensível."
          >
            Sensível
          </ChoiceRow>
        )}
      </div>

      {question.type === 'text' || question.type === 'number' ? (
        <FieldGrid>
          <Field
            label="Exemplo"
            optional
            id={fieldId('example')}
            error={errorAt('example')}
            hint="Aparece como dica no campo, como 'ex.: MPB, Marisa Monte'. Nunca um valor de teste."
          >
            <Input
              value={question.example}
              maxLength={200}
              onChange={(event) => update({ example: event.target.value })}
              onBlur={() => touch('example')}
            />
          </Field>
          {question.type === 'number' && !isClinic ? (
            <>
              <Field label="Mínimo" optional id={fieldId('min')} error={errorAt('min')}>
                <Input
                  inputMode="numeric"
                  value={question.min}
                  onChange={(event) => update({ min: event.target.value })}
                  onBlur={() => touch('min')}
                />
              </Field>
              <Field label="Máximo" optional id={fieldId('max')} error={errorAt('max')}>
                <Input
                  inputMode="numeric"
                  value={question.max}
                  onChange={(event) => update({ max: event.target.value })}
                  onBlur={() => touch('max')}
                />
              </Field>
            </>
          ) : null}
          {question.type === 'text' && !isClinic ? (
            <Field
              label="Tamanho máximo"
              optional
              id={fieldId('max')}
              error={errorAt('max')}
              hint="Em caracteres; sem valor, 2.000."
            >
              <Input
                inputMode="numeric"
                value={question.max}
                onChange={(event) => update({ max: event.target.value })}
                onBlur={() => touch('max')}
              />
            </Field>
          ) : null}
        </FieldGrid>
      ) : null}

      {choice ? (
        <section aria-labelledby={fieldId('options-title')} className="flex flex-col gap-3">
          <div className="flex items-baseline justify-between gap-3">
            <h3 id={fieldId('options-title')} className="eyebrow">
              Opções · {question.options.length} de {OPTION_LIMIT}
            </h3>
            {question.type === 'scale' ? (
              <span className="font-body text-eyebrow text-label font-light tracking-normal">
                da menor para a maior
              </span>
            ) : null}
          </div>
          {errorAt('options') ? (
            <p role="alert" className="font-body text-eyebrow text-plum leading-[1.5] font-light">
              {errorAt('options')}
            </p>
          ) : null}
          <div role="list" aria-label="Opções da pergunta" className="flex flex-col gap-2">
            {question.options.map((option, index) => (
              <div
                key={option.uid}
                role="listitem"
                className="border-hairline bg-surface flex flex-col gap-3 border px-4 py-3"
              >
                <FieldGrid>
                  <Field
                    label={`Opção ${index + 1}`}
                    id={fieldId(`options.${index}.label`)}
                    error={errorAt(`options.${index}.label`)}
                  >
                    <Input
                      value={option.label}
                      maxLength={80}
                      onChange={(event) => updateOption(index, { label: event.target.value })}
                      onBlur={() => blurOptionLabel(index)}
                    />
                  </Field>
                  {isClinic ? (
                    <Field
                      label="Exemplos"
                      optional
                      id={fieldId(`options.${index}.examples`)}
                      error={errorAt(`options.${index}.examples`)}
                      hint="Segunda linha da opção, como 'epilepsia, esclerose múltipla'."
                    >
                      <Input
                        value={option.examples}
                        maxLength={300}
                        onChange={(event) => updateOption(index, { examples: event.target.value })}
                        onBlur={() => touch(`options.${index}.examples`)}
                      />
                    </Field>
                  ) : (
                    <>
                      <Field
                        label="Valor"
                        id={fieldId(`options.${index}.value`)}
                        error={errorAt(`options.${index}.value`)}
                        hint="O que a resposta guarda, como anticoagulant. Não muda depois de publicado."
                      >
                        <Input
                          value={option.value}
                          maxLength={40}
                          autoComplete="off"
                          onChange={(event) => updateOption(index, { value: event.target.value })}
                          onBlur={() => touch(`options.${index}.value`)}
                        />
                      </Field>
                      <Field
                        label="Exemplos"
                        optional
                        id={fieldId(`options.${index}.examples`)}
                        error={errorAt(`options.${index}.examples`)}
                        hint="Segunda linha da opção, como 'epilepsia, esclerose múltipla'."
                      >
                        <Input
                          value={option.examples}
                          maxLength={300}
                          onChange={(event) =>
                            updateOption(index, { examples: event.target.value })
                          }
                          onBlur={() => touch(`options.${index}.examples`)}
                        />
                      </Field>
                    </>
                  )}
                </FieldGrid>
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                  {question.followUp?.when === 'options' ? (
                    <label className={inlineChoiceClass}>
                      <Checkbox
                        checked={option.opensFollowUp}
                        onCheckedChange={(checked) =>
                          updateOption(index, { opensFollowUp: checked === true })
                        }
                      />
                      abre o campo de descrição
                    </label>
                  ) : null}
                  {question.type === 'multiple_choice' ? (
                    <label className={inlineChoiceClass}>
                      <Checkbox
                        checked={option.isExclusive}
                        onCheckedChange={(checked) =>
                          updateOption(index, { isExclusive: checked === true })
                        }
                      />
                      exclusiva: desmarca as outras
                    </label>
                  ) : null}
                  {question.flagRule?.when === 'options' ? (
                    <label className={inlineChoiceClass}>
                      <Checkbox
                        checked={option.raisesFlag}
                        onCheckedChange={(checked) =>
                          updateOption(index, {
                            raisesFlag: checked === true,
                            attentionLabel: checked === true ? option.attentionLabel : '',
                          })
                        }
                      />
                      pede atenção
                    </label>
                  ) : null}
                  <span className="ml-auto flex items-center gap-4">
                    <ReorderControls
                      index={index}
                      count={question.options.length}
                      itemLabel={option.label.trim() || `opção ${index + 1}`}
                      onMove={(from, to) =>
                        update({ options: moveItem(question.options, from, to) })
                      }
                    />
                    <Button
                      variant="text"
                      size="sm"
                      aria-label={`Remover ${option.label.trim() || `opção ${index + 1}`}`}
                      disabled={question.options.length <= 2}
                      onClick={() =>
                        update({ options: question.options.filter((_, i) => i !== index) })
                      }
                    >
                      {question.options.length <= 2 ? 'mínimo de 2' : 'remover'}
                    </Button>
                  </span>
                </div>
                {option.raisesFlag ? (
                  <Field
                    label="Linha do bloco de atenção desta opção"
                    optional
                    id={fieldId(`options.${index}.attentionLabel`)}
                    error={errorAt(`options.${index}.attentionLabel`)}
                    hint="Sem ela, vale a linha da pergunta."
                  >
                    <Input
                      value={option.attentionLabel}
                      maxLength={120}
                      onChange={(event) =>
                        updateOption(index, { attentionLabel: event.target.value })
                      }
                      onBlur={() => touch(`options.${index}.attentionLabel`)}
                    />
                  </Field>
                ) : null}
              </div>
            ))}
          </div>
          <div>
            <Button
              variant="outline"
              size="sm"
              disabled={question.options.length >= OPTION_LIMIT}
              onClick={() => update({ options: [...question.options, emptyOption()] })}
            >
              {question.options.length >= OPTION_LIMIT
                ? `Limite de ${OPTION_LIMIT} opções`
                : '+ Adicionar opção'}
            </Button>
          </div>
        </section>
      ) : null}

      {question.type !== 'text' ? (
        <section className="border-hairline bg-surface border">
          <ChoiceRow
            bare
            control={
              <Checkbox
                checked={question.followUp !== null}
                onCheckedChange={(checked) => toggleFollowUp(checked === true)}
              />
            }
            description="Um campo de texto dentro da pergunta, como 'Quais, quando e onde?'."
          >
            Abre um campo de descrição
          </ChoiceRow>
          {question.followUp ? (
            <div className="border-hairline flex flex-col gap-4 border-t px-4 py-4">
              {followUpWhens(question.type).length > 1 ? (
                <Field
                  label="Quando abre"
                  id={fieldId('followUp.when')}
                  error={errorAt('followUp.when')}
                >
                  <RadioGroup
                    aria-label="Quando abre"
                    className="flex flex-wrap gap-x-6"
                    value={question.followUp.when}
                    onValueChange={(value) =>
                      update({
                        followUp: { ...question.followUp!, when: value as FollowUpWhen },
                        options:
                          value === 'options'
                            ? question.options
                            : question.options.map((option) => ({
                                ...option,
                                opensFollowUp: false,
                              })),
                      })
                    }
                  >
                    {followUpWhens(question.type).map((when) => (
                      <label key={when} className={inlineChoiceClass}>
                        <Radio value={when} />
                        {FOLLOW_UP_WHEN_LABELS[when]}
                      </label>
                    ))}
                  </RadioGroup>
                </Field>
              ) : null}
              <FieldGrid>
                <Field
                  label="O que descrever"
                  id={fieldId('followUp.label')}
                  error={errorAt('followUp.label')}
                  hint="O rótulo do campo, como 'A quê?'."
                >
                  <Input
                    value={question.followUp.label}
                    maxLength={120}
                    onChange={(event) =>
                      update({ followUp: { ...question.followUp!, label: event.target.value } })
                    }
                    onBlur={() => touch('followUp.label')}
                  />
                </Field>
                <Field
                  label="Exemplo"
                  id={fieldId('followUp.example')}
                  error={errorAt('followUp.example')}
                  hint="Uma resposta real, como 'ex.: dipirona; lidocaína'. Aparece como dica."
                >
                  <Input
                    value={question.followUp.example}
                    maxLength={200}
                    onChange={(event) =>
                      update({ followUp: { ...question.followUp!, example: event.target.value } })
                    }
                    onBlur={() => touch('followUp.example')}
                  />
                </Field>
              </FieldGrid>
              <label className={inlineChoiceClass}>
                <Checkbox
                  checked={question.followUp.isRequired}
                  onCheckedChange={(checked) =>
                    update({ followUp: { ...question.followUp!, isRequired: checked === true } })
                  }
                />
                a paciente precisa descrever para continuar
              </label>
            </div>
          ) : null}
        </section>
      ) : null}

      {flagRuleWhens(question.type).length > 0 ? (
        <section className="border-hairline bg-surface border">
          <ChoiceRow
            bare
            control={
              <Checkbox
                checked={question.flagRule !== null}
                onCheckedChange={(checked) => toggleFlagRule(checked === true)}
              />
            }
            description="Entra no bloco de atenção da leitura, antes do procedimento."
          >
            A resposta pede atenção da profissional
          </ChoiceRow>
          {question.flagRule ? (
            <div className="border-hairline flex flex-col gap-4 border-t px-4 py-4">
              {question.type === 'yes_no' ? (
                <Field
                  label="Quando"
                  id={fieldId('flagRule.when')}
                  error={errorAt('flagRule.when')}
                >
                  <RadioGroup
                    aria-label="Quando"
                    className="flex flex-wrap gap-x-6"
                    value={question.flagRule.when}
                    onValueChange={(value) =>
                      update({ flagRule: { ...question.flagRule!, when: value as FlagRuleWhen } })
                    }
                  >
                    <label className={inlineChoiceClass}>
                      <Radio value="yes" />
                      ao responder sim
                    </label>
                    <label className={inlineChoiceClass}>
                      <Radio value="no" />
                      ao responder não
                    </label>
                  </RadioGroup>
                </Field>
              ) : question.type === 'number' ? (
                <FieldGrid>
                  <Field
                    label="Quando"
                    id={fieldId('flagRule.when')}
                    error={errorAt('flagRule.when')}
                  >
                    <Select
                      value={question.flagRule.when}
                      onChange={(event) =>
                        update({
                          flagRule: {
                            ...question.flagRule!,
                            when: event.target.value as FlagRuleWhen,
                          },
                        })
                      }
                    >
                      <option value="above">acima de</option>
                      <option value="below">abaixo de</option>
                    </Select>
                  </Field>
                  <Field
                    label="Valor"
                    id={fieldId('flagRule.value')}
                    error={errorAt('flagRule.value')}
                  >
                    <Input
                      inputMode="numeric"
                      value={question.flagRule.value}
                      onChange={(event) =>
                        update({ flagRule: { ...question.flagRule!, value: event.target.value } })
                      }
                      onBlur={() => touch('flagRule.value')}
                    />
                  </Field>
                </FieldGrid>
              ) : (
                <p className="font-body text-eyebrow text-label font-light tracking-normal">
                  Nas opções marcadas com "pede atenção".
                  {errorAt('flagRule.when') ? (
                    <span role="alert" className="text-plum">
                      {' '}
                      {errorAt('flagRule.when')}
                    </span>
                  ) : null}
                </p>
              )}
              <Field
                label="Linha do bloco de atenção"
                id={fieldId('flagRule.attentionLabel')}
                meta={`${question.flagRule.attentionLabel.trim().length}/120`}
                error={errorAt('flagRule.attentionLabel')}
                hint="Como a profissional lê, por exemplo 'Usa anticoagulante contínuo'. {followUp} e {answer} entram no lugar da resposta."
              >
                <Input
                  value={question.flagRule.attentionLabel}
                  maxLength={120}
                  onChange={(event) =>
                    update({
                      flagRule: { ...question.flagRule!, attentionLabel: event.target.value },
                    })
                  }
                  onBlur={() => touch('flagRule.attentionLabel')}
                />
              </Field>
            </div>
          ) : null}
        </section>
      ) : null}

      {isClinic ? null : (
        <section className="border-hairline bg-surface border">
          <ChoiceRow
            bare
            control={
              <Checkbox
                checked={question.condition !== null}
                disabled={conditionSources.length === 0 && question.condition === null}
                onCheckedChange={(checked) =>
                  update({
                    condition:
                      checked === true
                        ? { questionKey: conditionSources[0]?.key ?? '', values: [] }
                        : null,
                  })
                }
              />
            }
            description={
              conditionSources.length === 0 && question.condition === null
                ? 'Não há pergunta anterior neste bloco que apareça sempre e tenha respostas fixas.'
                : "Condicional: entra logo após a pergunta que a dispara, com 'Apareceu porque você respondeu…'."
            }
          >
            Aparece só depois de uma resposta
          </ChoiceRow>
          {question.condition ? (
            <div className="border-hairline flex flex-col gap-4 border-t px-4 py-4">
              <Field
                label="Pergunta anterior"
                id={fieldId('condition.questionKey')}
                error={errorAt('condition.questionKey')}
              >
                <Select
                  value={question.condition.questionKey}
                  onChange={(event) =>
                    update({ condition: { questionKey: event.target.value, values: [] } })
                  }
                  placeholder="Escolha a pergunta"
                >
                  {conditionSources.map((source) => (
                    <option key={source.uid} value={source.key}>
                      {source.label.trim() || source.key}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field
                label="Aparece se a resposta for"
                id={fieldId('condition.values')}
                error={errorAt('condition.values')}
              >
                {conditionSource ? (
                  <div
                    role="group"
                    aria-label="Aparece se a resposta for"
                    className="flex flex-wrap gap-2"
                  >
                    {answerChoices(conditionSource).map((choice) => {
                      const pressed = question.condition!.values.includes(choice.value)
                      return (
                        <ChipToggle
                          key={choice.value}
                          pressed={pressed}
                          onPressedChange={(next) =>
                            update({
                              condition: {
                                ...question.condition!,
                                values: next
                                  ? [...question.condition!.values, choice.value]
                                  : question.condition!.values.filter((v) => v !== choice.value),
                              },
                            })
                          }
                        >
                          {choice.label || choice.value}
                        </ChipToggle>
                      )
                    })}
                  </div>
                ) : (
                  <p className="font-body text-eyebrow text-label font-light tracking-normal">
                    Escolha a pergunta anterior para ver as respostas dela.
                  </p>
                )}
              </Field>
            </div>
          ) : null}
        </section>
      )}

      {isClinic ? null : (
        <section aria-label="Revisão" className="border-hairline bg-surface border">
          <ChoiceRow
            bare
            control={
              <Checkbox
                checked={question.review.legal}
                onCheckedChange={(checked) =>
                  update({ review: { ...question.review, legal: checked === true } })
                }
              />
            }
            description="Sem as duas revisões a versão não publica (P-ANA-02)."
          >
            Revisão jurídica feita
          </ChoiceRow>
          <ChoiceRow
            bare
            control={
              <Checkbox
                checked={question.review.clinical}
                onCheckedChange={(checked) =>
                  update({ review: { ...question.review, clinical: checked === true } })
                }
              />
            }
          >
            Revisão clínica feita
          </ChoiceRow>
        </section>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="text" size="sm" onClick={onRemove}>
          remover pergunta
        </Button>
        <Button variant="outline" size="sm" onClick={onClose}>
          Fechar edição
        </Button>
      </div>
    </div>
  )
}

const inlineChoiceClass =
  'font-body text-body text-ink min-h-touch-desk max-desk:min-h-touch flex cursor-pointer items-center gap-2 font-light'

const FOLLOW_UP_WHEN_LABELS: Record<FollowUpWhen, string> = {
  yes: 'ao responder sim',
  no: 'ao responder não',
  always: 'sempre',
  options: 'nas opções marcadas',
}
